import React, { useCallback } from 'react';
import { useRouter } from 'expo-router';
import { useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { BodyText, Icon } from '@garnish/core';
import { Navbar } from '@garnish/navigation';

import { useTelemetry } from '@order/Telemetry';

import { useBagCount } from '../../../../hooks';
import { NAVBAR_BAG_ICON_SIZE } from '../../NavbarV2.constant';
import { navbarV2Messages as messages } from '../../NavbarV2.messages';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarBagButton = (props: Props) => {
  const { isLiquidGlassEnabled } = props;

  const router = useRouter();
  const { formatMessage } = useIntl();
  const { track } = useTelemetry();
  const { bagCount } = useBagCount();

  // ───────────────────────────────────────────────────────────────────────────

  const handleBagPress = useCallback(() => {
    track('nav_bar_bag_selected');
    router.navigate('/bag');
  }, [router, track]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <Navbar.Button
      aria-label={formatMessage(messages.bagAccessibilityLabel, { count: bagCount })}
      isLiquidGlassEnabled={isLiquidGlassEnabled}
      title={formatMessage(messages.bag)}
      onPress={handleBagPress}
    >
      <NavbarBagIcon bagCount={bagCount} />
    </Navbar.Button>
  );
};

// ─── Components ──────────────────────────────────────────────────────────────

const NavbarBagIcon = (props: NavbarBagIconProps) => {
  const { bagCount } = props;

  return (
    <View style={styles.bagIconContainer}>
      <Icon
        name="IconBagFill"
        width={NAVBAR_BAG_ICON_SIZE}
        height={NAVBAR_BAG_ICON_SIZE}
        color={theme.colors.WHITE}
      />

      <BodyText sizeMatch={['12']} style={styles.bagIconLabel} maxFontSizeMultiplier={1}>
        {bagCount > 9 ? '9+' : bagCount}
      </BodyText>
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  bagIconContainer: {
    marginLeft: theme.spacing['1'],
    marginTop: -2, // to better align with label
  },
  bagIconLabel: {
    position: 'absolute',
    right: 0,
    bottom: 1,
    left: 0,
    textAlign: 'center',
    color: theme.colors.KALE,
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type Props = { isLiquidGlassEnabled?: boolean };
type NavbarBagIconProps = { bagCount: number };
